import { useSortable } from "@dnd-kit/sortable";
import React, { useEffect, useState } from "react";
import { CSS } from "@dnd-kit/utilities";
import {
  FieldTypeType,
  OptionType,
  SubOptionType,
} from "../../../services/helpers/types";
import Button from "../Button/Button";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Add, DeleteOutline } from "@mui/icons-material";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import TextField from "../TextField/TextField";
import DraggableSelectSubOptionsList from "../DraggableSelectSubOptionsList/DraggableSelectSubOptionsList";
import { validateOptionValue } from "../../../services/helpers/commonHelpers";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

interface DraggableSelectOptionsItemProps {
  type?: FieldTypeType;
  id: number;
  options: OptionType[];
  option: OptionType | undefined;
  subOptionsVisible?: boolean;
  onOptionChange: (option: OptionType) => void;
  onDelete: (itemID: number) => void;
  onSubOptionsChange?: (option: SubOptionType) => void;
  onCreateSubOption?: (options?: OptionType) => void;
}

const DraggableSelectOptionsItem = (props: DraggableSelectOptionsItemProps) => {
  const {
    type = FieldTypeType.Select,
    id,
    options,
    option,
    subOptionsVisible,
    onDelete,
    onOptionChange,
    onCreateSubOption,
    onSubOptionsChange,
  } = props;

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const [optionName, setOptionName] = useState(option?.name ?? "");
  const [optionValue, setOptionValue] = useState(option?.value ?? "");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  useEffect(() => {
    setOptionName(option?.name ?? "");
    setOptionValue(option?.value ?? "");
  }, [option]);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleConfirmDelete = () => {
    if (option?.position !== undefined) {
      onDelete(option.position);
      setIsDeleteOpen(false);
    }
  };

  const handleOptionNameChange = (optionName: string) => {
    setOptionName(optionName);
    if (option) {
      const newOption: OptionType = {
        ...option,
        name: optionName,
      };
      onOptionChange(newOption);
    }
  };

  const handleOptionValueChange = (optionValue: string) => {
    if (option) {
      const newOption: OptionType = {
        ...option,
        value: optionValue,
      };
      if (!validateOptionValue(newOption, options, optionValue, dispatch)) {
        return;
      }

      onOptionChange(newOption);
      setOptionValue(optionValue);
    }
  };

  const handleCreateSubOption = (position: number) => {
    if (onCreateSubOption) {
      onCreateSubOption(options.find((item) => item.position === id));
    }
  };

  const handleSuboptionChange = (option: SubOptionType) => {
    if (onSubOptionsChange) {
      onSubOptionsChange(option);
    }
  };

  const handleSubOptionPositionChange = (newItemsPositions: number[]) => {
    if (option && option.subOptions) {
      const subOptions = option.subOptions;

      const sortedObjects: SubOptionType[] = newItemsPositions.map(
        (num, index) => {
          const subOption = subOptions.find((item) => item.position === num);
          if (!subOption) {
            return subOptions[index];
          }
          return {
            ...subOption,
            position: index + 1,
          };
        }
      );

      const newOption: OptionType = {
        ...option,
        subOptions: sortedObjects,
      };
      onOptionChange(newOption);
    }
  };

  const handleSubOptionDelete = (itemID: number) => {
    if (option?.subOptions) {
      const deleteNewOptions = option.subOptions
        .filter((option) => option.position !== itemID)
        .map((value, index) => {
          return { ...value, position: index + 1 };
        });

      const newOption: OptionType = {
        ...option,
        subOptions: deleteNewOptions.length > 0 ? deleteNewOptions : undefined,
      };
      onOptionChange(newOption);
    }
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="dui-options-edit-item">
        <div className="dui-options-edit-item-buttons">
          <div {...attributes} {...listeners} className="dui-btn-inner">
            <Button icon={<UnfoldMoreIcon />} type="text" />
          </div>
          <div className="dui-btn-inner">
            <Button
              icon={<DeleteOutline />}
              type="text"
              onClick={() => {
                setIsDeleteOpen(true);
              }}
            />
          </div>
        </div>
        <div className="dui-options-edit-item-name">
          <div className="dui-options-edit-item-name-field">
            <div>{t("optionName")}:</div>
            <TextField value={optionName} onChange={handleOptionNameChange} />
          </div>
          <div className="dui-options-edit-item-name-dash">-</div>
          <div className="dui-options-edit-item-name-field">
            <div>{t("reportValue")}:</div>
            <TextField value={optionValue} onChange={handleOptionValueChange} />
          </div>
        </div>
        <ConfirmDialog
          open={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={handleConfirmDelete}
          title={t("deleteTitle")}
          description={`${t("areYouSureDelete1")} ${option?.name}${t(
            "areYouSureDelete2"
          )}`}
          confirmText={t("delete")}
          cancelText={t("cancel")}
          confirmButtonAppearance="error"
        />
      </div>
      {type === FieldTypeType.Cascader && subOptionsVisible ? (
        <div className="dui-options-edit-item-sub-options">
          <DraggableSelectSubOptionsList
            options={
              options.find((item) => item.position === id)?.subOptions ?? []
            }
            onChangePosition={handleSubOptionPositionChange}
            onDeleteItem={handleSubOptionDelete}
            onOptionChange={handleSuboptionChange}
          />
          <div
            className="dui-fields-edit-add-button-wrapper"
            onClick={() => handleCreateSubOption(id)}
          >
            <Button
              icon={<Add />}
              type="primary"
              corners="circle"
              className="dui-fields-edit-add-button"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DraggableSelectOptionsItem;
